import { IClassListItem } from "../model/respone/classes/IClassListItem";

/** Usage 1 nhóm tự chọn: tập môn gốc phân biệt đã ĐK + tổng TC. */
interface INhomTuChonUsage {
    mons: Set<number>;
    tc: number;
}

/** Môn gốc hiệu lực của 1 lớp (lớp môn tương đương quy về môn gốc để đếm đúng nhóm). */
const effMonOf = (c: IClassListItem): number =>
    c.id_mon_goc && c.id_mon_goc > 0 ? c.id_mon_goc : c.id_mon;

/**
 * Đếm số môn phân biệt + tổng TC đã ĐK theo từng nhóm tự chọn, từ danh sách lớp + tập lớp đã ĐK.
 * Chỉ tính lớp hiển thị trong `list` (backend đã ẩn children LTH → mỗi môn đếm 1 lần, TC lấy của
 * lớp parent). Mirror backend `ClassListService.BuildNhomTuChonUsage`.
 */
export function buildNhomTuChonUsage(
    list: IClassListItem[],
    registeredClassIds: Set<number>
): Map<number, INhomTuChonUsage> {
    const usage = new Map<number, INhomTuChonUsage>();
    // Seed baseline LUỸ KẾ CẢ KHOÁ: môn nhóm tự chọn SV đã học & chốt các đợt trước (history, đã sync
    // STU) — backend gửi trên mỗi item của nhóm. Seed 1 lần/nhóm TRƯỚC khi cộng session → cap tính gộp
    // toàn CTĐT, khớp backend BuildNhomTuChonUsage(session ∪ history). Nhóm không có history → bỏ qua.
    for (const c of list) {
        const group = c.nhom_tu_chon ?? 0;
        if (group <= 0 || usage.has(group)) continue;
        const histMons = c.nhom_tu_chon_history_mons;
        if (histMons && histMons.length > 0) {
            usage.set(group, { mons: new Set<number>(histMons), tc: c.nhom_tu_chon_history_tc ?? 0 });
        }
    }
    for (const c of list) {
        const group = c.nhom_tu_chon ?? 0;
        if (group <= 0 || !registeredClassIds.has(c.classId)) continue;
        let u = usage.get(group);
        if (!u) { u = { mons: new Set<number>(), tc: 0 }; usage.set(group, u); }
        const eff = effMonOf(c);
        if (!u.mons.has(eff)) { u.mons.add(eff); u.tc += c.so_tin_chi ?? 0; }
    }
    return usage;
}

/**
 * Lớp `c` có bị chặn vì nhóm tự chọn đã đủ không? Trả message (để hiển thị + ẩn nút) hoặc null.
 * Cap = field trên chính item (`so_mon_dang_ky` / `so_tin_chi_tu_chon`); backend chỉ gửi >0 khi đợt
 * chính khoá → item ở đợt khác luôn = 0 = không áp cap. Môn đã nằm trong nhóm (đã ĐK lớp khác cùng
 * môn) → không tự chặn (Rule 12 lo). Mirror backend cap logic.
 */
export function nhomTuChonBlock(
    c: IClassListItem,
    usage: Map<number, INhomTuChonUsage>
): string | null {
    const group = c.nhom_tu_chon ?? 0;
    if (group <= 0) return null;
    const capMon = c.so_mon_dang_ky ?? 0;
    const capTc = c.so_tin_chi_tu_chon ?? 0;
    if (capMon <= 0 && capTc <= 0) return null;

    const u = usage.get(group);
    if (u && u.mons.has(effMonOf(c))) return null;
    const monCount = u ? u.mons.size : 0;
    const tcSum = u ? u.tc : 0;
    const name = c.nhom_tu_chon_text ?? "";
    if (capMon > 0 && monCount >= capMon) return `Đã đủ số môn tự chọn của nhóm "${name}" (tối đa ${capMon} môn).`;
    if (capTc > 0 && tcSum + (c.so_tin_chi ?? 0) > capTc) return `Vượt số tín chỉ tự chọn của nhóm "${name}" (tối đa ${capTc} TC).`;
    return null;
}
